import { useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { ArrowLeft, ExternalLink, LogIn, Pencil, RotateCcw, ShieldOff, Trash2 } from 'lucide-react'
import { apiFetch } from '../lib/api'
import type { ActivityRow, Paged } from '../lib/types'
import { PageHeader, Field, formatDate } from '../components/page'
import { ErrorState, Spinner, EmptyState } from '../components/states'
import { StatusBadge, Badge } from '../components/badge'
import { ConfirmDialog } from '../components/confirm-dialog'
import { EditUserDialog } from '../components/edit-user-dialog'
import { DeactivateUserDialog, EraseUserDialog, REFUND_LABELS, RestoreUserDialog } from '../components/delete-user-dialogs'

interface UserDetail {
	user: Record<string, any>
	billing: Record<string, any> | null
	instances: any[]
	legacySubscriptions: any[]
	activeSessions: any[]
	chargebeeCustomerUrl: string | null
}

export function UserDetailPage() {
	const { id = '' } = useParams()
	const queryClient = useQueryClient()
	const [dialog, setDialog] = useState<'impersonate' | 'revoke' | 'edit' | 'deactivate' | 'restore' | 'erase' | null>(null)
	const [notice, setNotice] = useState<string | null>(null)

	const { data, isPending, error, refetch } = useQuery({
		queryKey: ['user', id],
		queryFn: () => apiFetch<UserDetail>(`/api/admin/users/${id}`),
	})

	const timeline = useQuery({
		queryKey: ['timeline', 'user', id],
		queryFn: () => apiFetch<Paged<ActivityRow>>(`/api/admin/logs/timeline/user/${id}?limit=15`),
	})

	const impersonate = useMutation({
		mutationFn: () => apiFetch<{ url: string }>(`/api/admin/users/${id}/impersonate`, { method: 'POST' }),
		onSuccess: ({ url }) => {
			setDialog(null)
			// New tab: the ticket is single-use with a 60-second TTL, so it has to
			// be opened immediately, and the operator keeps the dashboard open.
			window.open(url, '_blank', 'noopener')
		},
	})

	const revoke = useMutation({
		mutationFn: () => apiFetch<{ revoked: number }>(`/api/admin/users/${id}/revoke-sessions`, { method: 'POST' }),
		onSuccess: ({ revoked }) => {
			setDialog(null)
			setNotice(`Revoked ${revoked} session${revoked === 1 ? '' : 's'}.`)
			queryClient.invalidateQueries({ queryKey: ['user', id] })
		},
	})

	if (isPending) return <Spinner />
	if (error) return <ErrorState error={error} onRetry={refetch} />

	const { user, billing, instances, legacySubscriptions, activeSessions, chargebeeCustomerUrl } = data
	const erased = Boolean(user.erasedAt)
	const deactivated = !erased && Boolean(user.deactivatedAt)
	const refreshUser = () => {
		queryClient.invalidateQueries({ queryKey: ['user', id] })
		queryClient.invalidateQueries({ queryKey: ['timeline', 'user', id] })
		queryClient.invalidateQueries({ queryKey: ['users'] })
		queryClient.invalidateQueries({ queryKey: ['overview'] })
	}

	return (
		<>
			<Link to="/users" className="mb-4 inline-flex items-center gap-1.5 text-sm text-ink-soft hover:underline">
				<ArrowLeft className="h-4 w-4" /> Users
			</Link>

			<PageHeader
				title={erased ? `Deleted user #${user.id}` : user.email}
				subtitle={erased ? undefined : user.name || undefined}
				actions={
					<>
						{chargebeeCustomerUrl && (
							<a href={chargebeeCustomerUrl} target="_blank" rel="noreferrer" className="btn-secondary">
								<ExternalLink className="h-4 w-4" /> Chargebee
							</a>
						)}
						{deactivated && (
							<>
								<button type="button" className="btn-secondary" onClick={() => setDialog('edit')}>
									<Pencil className="h-4 w-4" /> Edit
								</button>
								<button type="button" className="btn-danger" onClick={() => setDialog('erase')}>
									<Trash2 className="h-4 w-4" /> Delete now
								</button>
								<button type="button" className="btn-primary" onClick={() => setDialog('restore')}>
									<RotateCcw className="h-4 w-4" /> Restore
								</button>
							</>
						)}
						{!erased && !deactivated && (
							<>
								<button type="button" className="btn-secondary" onClick={() => setDialog('edit')}>
									<Pencil className="h-4 w-4" /> Edit
								</button>
								<button type="button" className="btn-secondary" onClick={() => setDialog('revoke')}>
									<ShieldOff className="h-4 w-4" /> Revoke sessions
								</button>
								<button type="button" className="btn-danger" onClick={() => setDialog('deactivate')}>
									<Trash2 className="h-4 w-4" /> Delete
								</button>
								<button type="button" className="btn-primary" onClick={() => setDialog('impersonate')}>
									<LogIn className="h-4 w-4" /> Impersonate
								</button>
							</>
						)}
					</>
				}
			/>

			{erased && (
				<p className="mb-4 rounded-lg border border-line bg-surface px-3 py-2 text-sm text-ink-soft">
					This account was deleted on {formatDate(user.erasedAt)} at the customer's request. Their personal details have
					been removed; invoices are kept in Chargebee for the legal retention period.
				</p>
			)}
			{deactivated && (
				<div className="mb-4 rounded-lg bg-amber-50 px-3 py-2 text-sm text-amber-900">
					<p>
						<span className="font-semibold">Deactivated</span> on {formatDate(user.deactivatedAt)}
						{user.deactivation?.requestedByCustomer
							? ' by the customer from their profile page'
							: user.deactivation?.byAdminEmail
								? ` by ${user.deactivation.byAdminEmail}`
								: ''}
						. The customer cannot sign in.
						Everything will be <span className="font-semibold">deleted permanently on {formatDate(user.erasureScheduledAt)}</span>{' '}
						unless you restore the account.
					</p>
					<p className="mt-1">
						Refund at deletion: {REFUND_LABELS[user.deactivation?.refund === 'prorate' ? 'prorate' : 'none']}
					</p>
					{user.deactivation?.reason && <p className="mt-1">Reason: {user.deactivation.reason}</p>}
					{user.deactivation?.erasureError && (
						<p className="mt-1 text-red-700">
							The last deletion attempt failed and will be retried daily: {user.deactivation.erasureError}
						</p>
					)}
				</div>
			)}

			{notice && <p className="mb-4 rounded-lg bg-emerald-50 px-3 py-2 text-sm text-emerald-800">{notice}</p>}
			{impersonate.error && (
				<p className="mb-4 rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700">{(impersonate.error as Error).message}</p>
			)}

			<div className="grid gap-4 lg:grid-cols-3">
				<section className="card p-4 lg:col-span-2">
					<h2 className="mb-3 text-sm font-semibold">Account</h2>
					<dl className="grid grid-cols-2 gap-4 sm:grid-cols-3">
						<Field label="Name">{user.name || null}</Field>
						<Field label="Company">{user.company}</Field>
						<Field label="Phone">{user.phone}</Field>
						<Field label="Language">{user.language}</Field>
						<Field label="Source">{user.source}</Field>
						<Field label="Two-factor">{user.twoFactorEnabled ? 'Enabled' : 'Disabled'}</Field>
						<Field label="Marketing consent">{user.marketingConsent ? 'Yes' : 'No'}</Field>
						<Field label="Chargebee customer"><span className="mono">{user.chargebeeCustomerId}</span></Field>
						<Field label="WHMCS id">{user.whmcsId}</Field>
						<Field label="Created">{formatDate(user.createdAt)}</Field>
						<div className="col-span-2 sm:col-span-3">
							<Field label="Address">
								{user.addressLine1
									? [
											`${user.addressFirstName ?? ''} ${user.addressLastName ?? ''}`.trim(),
											user.addressLine1,
											user.addressLine2,
											[user.addressZip, user.addressCity].filter(Boolean).join(' '),
											user.addressState,
											user.addressCountry,
										]
											.filter(Boolean)
											.join(', ')
									: null}
							</Field>
						</div>
					</dl>
				</section>

				<section className="card p-4">
					<h2 className="mb-3 text-sm font-semibold">Billing address</h2>
					{billing ? (
						<dl className="grid gap-3">
							<Field label="Name">{`${billing.firstName} ${billing.lastName}`.trim()}</Field>
							<Field label="Address">{[billing.line1, billing.line2].filter(Boolean).join(', ')}</Field>
							<Field label="City">{[billing.zip, billing.city].filter(Boolean).join(' ')}</Field>
							<Field label="Country">{billing.country}</Field>
							<Field label="VAT">{billing.vatNumber}</Field>
						</dl>
					) : (
						<p className="text-sm text-ink-faint">No billing address on file.</p>
					)}
				</section>

				<section className="card lg:col-span-3">
					<h2 className="border-b border-line p-4 text-sm font-semibold">Instances ({instances.length})</h2>
					{instances.length === 0 ? (
						<EmptyState message="No instances." />
					) : (
						<ul className="divide-y divide-line">
							{instances.map((i) => (
								<li key={i.id} className="flex flex-wrap items-center justify-between gap-3 p-4">
									<div className="min-w-0">
										<Link to={`/subscriptions/${i.id}`} className="font-medium hover:underline">
											{i.planSlug} · {i.billingTerm ?? '—'}
										</Link>
										<div className="mono truncate">{i.hostingdeId ?? 'not provisioned'}</div>
									</div>
									<StatusBadge value={i.status} />
								</li>
							))}
						</ul>
					)}
				</section>

				{legacySubscriptions.length > 0 && (
					<section className="card lg:col-span-3">
						<h2 className="border-b border-line p-4 text-sm font-semibold">
							Legacy subscriptions ({legacySubscriptions.length})
						</h2>
						<ul className="divide-y divide-line">
							{legacySubscriptions.map((s) => (
								<li key={s.id} className="flex flex-wrap items-center justify-between gap-3 p-4">
									<div className="min-w-0">
										<div className="truncate font-medium">{s.productName ?? s.planSlug}</div>
										<div className="mono">{s.provider} · WHMCS #{s.whmcsServiceId ?? '—'}</div>
									</div>
									<StatusBadge value={s.status} />
								</li>
							))}
						</ul>
					</section>
				)}

				<section className="card lg:col-span-2">
					<h2 className="border-b border-line p-4 text-sm font-semibold">Activity</h2>
					{timeline.isPending ? (
						<Spinner />
					) : timeline.error ? (
						<ErrorState error={timeline.error} />
					) : timeline.data.rows.length === 0 ? (
						<EmptyState message="Nothing recorded yet." />
					) : (
						<ul className="divide-y divide-line">
							{timeline.data.rows.map((row) => (
								<li key={row.id} className="flex items-start justify-between gap-3 p-3">
									<div className="min-w-0">
										<div className="truncate text-sm">{row.summary}</div>
										<div className="mono">{formatDate(row.createdAt)}</div>
									</div>
									<Badge tone={row.actorType === 'admin' ? 'info' : row.actorType === 'system' ? 'warning' : 'neutral'}>
										{row.actorType}
									</Badge>
								</li>
							))}
						</ul>
					)}
				</section>

				<section className="card">
					<h2 className="border-b border-line p-4 text-sm font-semibold">
						Active sessions ({activeSessions.length})
					</h2>
					{activeSessions.length === 0 ? (
						<EmptyState message="No active sessions." />
					) : (
						<ul className="divide-y divide-line">
							{activeSessions.map((s) => (
								<li key={s.id} className="p-3">
									<div className="flex items-center gap-2 text-sm">
										{s.ipAddress ?? 'unknown ip'}
										{s.impersonatedByAdminId && <Badge tone="warning">impersonated</Badge>}
									</div>
									<div className="mono">expires {formatDate(s.expiresAt)}</div>
								</li>
							))}
						</ul>
					)}
				</section>
			</div>

			{dialog === 'deactivate' && (
				<DeactivateUserDialog
					userId={id}
					email={user.email}
					runningInstances={instances.filter((i) => ['active', 'cancelling', 'pending'].includes(i.status)).length}
					billedSubscriptions={instances.filter((i) => i.chargebeeSubscriptionId && ['active', 'pending'].includes(i.status)).length}
					onClose={() => setDialog(null)}
					onDone={refreshUser}
				/>
			)}
			{dialog === 'erase' && (
				<EraseUserDialog
					userId={id}
					email={user.email}
					defaultRefund={user.deactivation?.refund === 'prorate' ? 'prorate' : 'none'}
					onClose={() => setDialog(null)}
					onDone={refreshUser}
				/>
			)}
			{dialog === 'restore' && (
				<RestoreUserDialog userId={id} email={user.email} onClose={() => setDialog(null)} onDone={refreshUser} />
			)}

			{dialog === 'edit' && (
				<EditUserDialog
					user={user as Parameters<typeof EditUserDialog>[0]['user']}
					onClose={() => setDialog(null)}
					onSaved={() => {
						queryClient.invalidateQueries({ queryKey: ['user', id] })
						queryClient.invalidateQueries({ queryKey: ['timeline', 'user', id] })
						queryClient.invalidateQueries({ queryKey: ['users'] })
					}}
				/>
			)}

			<ConfirmDialog
				open={dialog === 'impersonate'}
				title={`Sign in as ${user.email}?`}
				description={
					<>
						<p>A new tab will open with you signed in to the customer app as this person. Anything you do there is done as them.</p>
						<p className="mt-2">The session lasts 30 minutes and both the request and the sign-in are recorded in the activity log.</p>
						<p className="mt-2 font-medium text-ink">This replaces your own customer-app session in this browser.</p>
					</>
				}
				confirmLabel="Impersonate"
				busy={impersonate.isPending}
				onConfirm={() => impersonate.mutate()}
				onCancel={() => setDialog(null)}
			/>

			<ConfirmDialog
				open={dialog === 'revoke'}
				title="Revoke all sessions?"
				description={`${user.email} will be signed out everywhere and must sign in again.`}
				confirmLabel="Revoke"
				tone="danger"
				busy={revoke.isPending}
				onConfirm={() => revoke.mutate()}
				onCancel={() => setDialog(null)}
			/>
		</>
	)
}
